import { useCallback, useRef } from 'react';
import type { ImageViewerAction } from '../imageViewerReducer';

interface UseImagePanOptions {
  dispatch: React.Dispatch<ImageViewerAction>;
  getState: () => { panX: number; panY: number; zoom: number };
  disabled?: boolean;
}

export function useImagePan({ dispatch, getState, disabled }: UseImagePanOptions) {
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || e.button !== 0) return;
      // Don't start panning if clicking a button or control
      if ((e.target as HTMLElement).closest('button, input, [role="toolbar"]')) return;

      const state = getState();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPanX: state.panX,
        startPanY: state.panY,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [disabled, getState],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      dispatch({
        type: 'PAN',
        panX: dragRef.current.startPanX + dx,
        panY: dragRef.current.startPanY + dy,
      });
    },
    [dispatch],
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp };
}
